import { useCallback, useEffect, useRef, useState } from "react";
import { useLatestRef } from "@/hooks/useLatestRef";

/** `null` removes the key; an array writes one `key=value` per entry. */
export type QueryParamUpdates = Record<string, string | string[] | null>;

type QueuedWrite = {
  updates: QueryParamUpdates;
  clearHash: boolean;
  pathname: string;
};

/**
 * The params the last replace wrote, valid only while still on `pathname`.
 * `ownSearches` holds every query this burst started from or wrote; a URL
 * outside that set was changed by someone else (e.g. an in-page Link). It is
 * emptied on popstate: back/forward can land on one of those queries, and
 * that is a navigation, not the replace still echoing.
 */
type SettlingWrite = {
  params: URLSearchParams;
  ownSearches: Set<string>;
  pathname: string;
  since: number;
};

const currentSearch = () =>
  new URLSearchParams(window.location.search).toString();

type QueryRouter = {
  replace: (href: string, options?: { scroll?: boolean }) => void;
};

/** Overlapping `router.replace` calls trigger Next's "Cancel rendering route". */
const SETTLE_MS = 50;
/** Upper bound on waiting for the browser URL to reflect the last replace. */
const MAX_ECHO_WAIT_MS = 1000;

/**
 * Serializes query-param writes through `router.replace`: one replace per
 * animation frame, then a settle window before the next. Writes made while a
 * replace is settling are merged and flushed afterwards on top of the params
 * that replace wrote, so a burst of writes lands in order with none dropped.
 * Writes queued for a route the page has since left are dropped, and a
 * navigation that changes the query underneath a settling replace becomes the
 * base for later writes instead of being overwritten by the replaced params.
 *
 * `isWriting` is true from the first queued write until the browser URL
 * reflects the last replace (bounded by MAX_ECHO_WAIT_MS); the URL echoes seen
 * in that window are our own.
 */
export function useQueryParamWriter(router: QueryRouter) {
  const routerRef = useLatestRef(router);
  const queuedRef = useRef<QueuedWrite | null>(null);
  const settlingRef = useRef<SettlingWrite | null>(null);
  const frameRef = useRef<number | null>(null);
  const timerRef = useRef<number | null>(null);
  const [isWriting, setIsWriting] = useState(false);

  useEffect(() => {
    const onPopState = () => settlingRef.current?.ownSearches.clear();
    window.addEventListener("popstate", onPopState);
    return () => {
      window.removeEventListener("popstate", onPopState);
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    };
  }, []);

  const flush = useCallback(
    function flush() {
      frameRef.current = null;
      timerRef.current = null;
      const pathname = window.location.pathname;
      const search = currentSearch();
      const queued = queuedRef.current;
      let settling =
        settlingRef.current?.pathname === pathname ? settlingRef.current : null;
      if (settling && !settling.ownSearches.has(search)) {
        settling = null;
        settlingRef.current = null;
      }
      const routeLeft = queued !== null && queued.pathname !== pathname;
      if (queued && !routeLeft) {
        queuedRef.current = null;
        const params = settling?.params ?? new URLSearchParams(search);
        for (const [key, value] of Object.entries(queued.updates)) {
          if (Array.isArray(value)) {
            params.delete(key);
            for (const entry of value) params.append(key, entry);
          } else if (value === null) {
            params.delete(key);
          } else {
            params.set(key, value);
          }
        }
        const hash = queued.clearHash ? "" : window.location.hash;
        routerRef.current.replace(
          `${queued.pathname}?${params.toString()}${hash}`,
          { scroll: false },
        );
        const ownSearches = settling?.ownSearches ?? new Set([search]);
        ownSearches.add(params.toString());
        settlingRef.current = {
          params,
          ownSearches,
          pathname,
          since: Date.now(),
        };
        timerRef.current = window.setTimeout(flush, SETTLE_MS);
        return;
      }
      const echoPending =
        settling !== null &&
        !routeLeft &&
        Date.now() - settling.since < MAX_ECHO_WAIT_MS &&
        search !== settling.params.toString();
      if (echoPending) {
        timerRef.current = window.setTimeout(flush, SETTLE_MS);
        return;
      }
      queuedRef.current = null;
      settlingRef.current = null;
      setIsWriting(false);
    },
    [routerRef],
  );

  const updateQueryParams = useCallback(
    (updates: QueryParamUpdates, clearHash = false) => {
      const queued = queuedRef.current;
      if (queued && queued.pathname === window.location.pathname) {
        Object.assign(queued.updates, updates);
        queued.clearHash ||= clearHash;
        return;
      }
      queuedRef.current = {
        updates: { ...updates },
        clearHash,
        pathname: window.location.pathname,
      };
      // A settle timer is always pending while a replace is settling; it
      // flushes this write (on the current URL if the route changed).
      if (settlingRef.current) return;
      setIsWriting(true);
      frameRef.current = requestAnimationFrame(flush);
    },
    [flush],
  );

  return { updateQueryParams, isWriting };
}
